import { toRaw, watch } from "vue";
import lodash from "lodash";
import moment from "moment";
import { Modal, notification } from "ant-design-vue";
import i18n from "../i18n";
import router from "../router";
import { has } from "./menu-context";
import { addRules, clearRulesStartsWith } from "./validate";
import { randomUuid } from "./util";
import store from "../store";

export const initUseForm = ({ data, page, routerPrefix }) => {
    const record = page.data?.[0];
    if (record) {
        lodash.merge(data.useForm.initialModel, lodash.cloneDeep(toRaw(record)));
        lodash.merge(data.useForm.modelRef, lodash.cloneDeep(toRaw(record)));
    } else {
        notification["error"]({ message: i18n.global.t("views.error.404.title") });
        if (routerPrefix) {
            router.push({ name: `${routerPrefix}` });
        } else {
            router.push({ name: `404` });
        }
    }
};

export const onClickCreate = ({ routerPrefix, params = { props: JSON.stringify({ forceDefault: false }) } }) => {
    const userInfo = store.state.resource.auth.info?.user;
    if (userInfo?.refPersonType?.key === "2") {
        const expireDate = moment(userInfo.expireDate, "YYYY-MM-DD");
        if (expireDate.isValid()) {
            const today = moment(moment().format("YYYY-MM-DD"), "YYYY-MM-DD");
            if (today.isAfter(expireDate)) {
                // IdCard Expire
                notification["error"]({ message: i18n.global.t("error.form.idCardExpire") });
                return;
            }
        }
    }
    router.push({ name: `${routerPrefix}-create`, params });
};

export const onClickView = ({ routerPrefix, record, props }) => {
    if (props) {
        router.push({ name: `${routerPrefix}-view`, params: { id: record.id, props: JSON.stringify({...props, menuContext: undefined, viewOnly: true}) }  });
    } else {
        router.push({ name: `${routerPrefix}-view`, params: { id: record.id }  });
    }
};

export const onClickEdit = ({ routerPrefix, record, props }) => {
    if(props) {
        router.push({ name: `${routerPrefix}-edit`, params: { id: record.id, props: JSON.stringify({ ...props, menuContext: undefined, viewOnly: false}) }  });
    } else {
        router.push({ name: `${routerPrefix}-edit`, params: { id: record.id } });
    }
};

export const onClickActive = ({ record, callActive, onClickEdit, setIsLoading }) => {
    Modal.confirm({
        title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.active") }),
        autoFocusButton: "cancel",
        onOk: () => {
            setIsLoading(true);
            callActive({ id: record.id }).finally(() => {
                setIsLoading(false);
                onClickEdit({ record });
            });
        }
    });
};

export const onClickArchive = ({ record, callArchive, onClickView, setIsLoading }) => {
    Modal.confirm({
        title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.archive") }),
        autoFocusButton: "cancel",
        onOk: () => {
            setIsLoading(true);
            callArchive({ id: record.id }).finally(() => {
                setIsLoading(false);
                onClickView({ record });
            });
        },
        okType: "danger"
    });
};

export const onClickDelete = ({ record, callDelete, state, routerPrefix, setIsLoading, routerParams }) => {
    Modal.confirm({
        title: i18n.global.t("components.table.operatorAndPagination.youAreWant", { text: i18n.global.t("button.delete") }),
        autoFocusButton: "cancel",
        onOk: () => {
            setIsLoading(true);
            callDelete({ id: record.id }).finally(() => {
                setIsLoading(false);
                state.page.page = 0;
                router.push({ name: routerPrefix, params: routerParams });
            });
        },
        okType: "danger"
    });
};

export const onClickActiveAll = ({ rowSelection, callActiveAll, state, onSearch, setIsLoading }) => {
    setIsLoading(true);
    return callActiveAll({ ids: rowSelection.selectedRowKeys })
        .then(() => onSearch(state))
        .finally(() => setIsLoading(false));
};

export const onClickArchiveAll = ({ rowSelection, callArchiveAll, state, onSearch, setIsLoading }) => {
    setIsLoading(true);
    return callArchiveAll({ ids: rowSelection.selectedRowKeys })
        .then(() => onSearch(state))
        .finally(() => setIsLoading(false));
};

export const onClickDeleteAll = ({ rowSelection, callDeleteAll, state, onSearch, setIsLoading }) => {
    setIsLoading(true);
    return callDeleteAll({ ids: rowSelection.selectedRowKeys })
        .then(() => {
            state.page.page = 0;
            onSearch(state);
        })
        .finally(() => setIsLoading(false));
};

export const onClickDeleteAllClient = ({ rowSelection, useForm, items, key }) => {
    rowSelection.selectedRows.forEach(row => {
        if (row.id) {
            const found = lodash.find(items, item => lodash.isEqual(item, row));
            if (found) {
                found.isDelete = true;
            }
        } else {
            const foundIndex = items.findIndex(item => lodash.isEqual(item, row));
            if (foundIndex >= 0) {
                if (useForm) {
                    Promise.resolve()
                        .then(() => clearRulesStartsWith({ useForm, key: `${key}.` }))
                        .then(() => lodash.remove(items, item => lodash.isEqual(item, row)));
                } else {
                    items.splice(foundIndex, 1);
                }
            }
        }
    });
};

export const onCallChangeLog = ({ id, page, size, createSearchOneCriteria, callChanges }) => {
    const criteria = createSearchOneCriteria({ id, forLog: true });
    //criteria.select.push('refUpdateBy.fullName')
    criteria.page = page;
    criteria.size = size;
    criteria.where = [];
    return callChanges({ id, criteria });
};

export const checkAttribute = ({ props, data }) => {
    const { menuContext, viewOnly } = props;
    const { id } = data;
    let isForbidden = false;
    if (viewOnly && !has({ menuContext, key: "canView" })) {
        isForbidden = true;
    } else if (!viewOnly) {
        if (id && !has({ menuContext, key: "canEdit" })) {
            isForbidden = true;
        } else if (!id && !has({ menuContext, key: "canCreate" })) {
            isForbidden = true;
        }
    }
    if (isForbidden) {
        router.push({ name: "403" });
    }
};

export const conditionFormChecker = ({ props, data, executeKey, params }) => {
    const { useForm, conditionForms } = data;
    const { modelRef, rulesRef, initialModel } = useForm;
    const result = conditionForms
        ?.find(condition => condition.check({ props, data, modelRef, rulesRef, initialModel, ...params }) && condition[executeKey])
        // eslint-disable-next-line no-unexpected-multiline
        ?.[executeKey]?.({ props, data, modelRef, rulesRef, initialModel, ...params });
    return result === undefined ? true : result;
};

export const putField = ({ data, fields }) => {
    if (!data.fieldLogs) data.fieldLogs = [];
    Promise.resolve().then(() => {
        fields
            .filter(d => d.key)
            .forEach(d => {
                const index = data.fieldLogs.findIndex(d2 => d.key === d2.key);
                /*
            const visible = d.visible?.({ field: d })
            if (d.visible === undefined || visible === true) {
                if (index < 0) {
                    data.fieldLogs.push(d)
                }
            } else {
                if (index >= 0) {
                    data.fieldLogs.splice(index, 1)
                }
            }
            */
                if (index < 0) {
                    data.fieldLogs.push(d);
                }
            });
        data.fieldLogs.sort((o1, o2) => (o1.changeLog?.getValueChildren ? 1 : 0) - (o2.changeLog?.getValueChildren ? 1 : 0));
    });
    return fields;
};

export const copyForm = ({ form }) => {
    form = lodash.cloneDeep(form);
    form._id = form.id || randomUuid();
    form.id = null;
    form.version = null;
    return lodash.cloneDeepWith(form, value => {
        if (Array.isArray(value)) {
            return value.forEach(value2 => {
                value2._id = value2.id || randomUuid();
                value2.id = null;
                value2.version = null;
            });
        }
    });
};

export const createRulesChildren = ({ data, key, rules, itemsKey }) => {
    const { useForm } = data;
    const prefix = key ? `${key}.` : "";
    let itemsKeys = itemsKey;
    if (!Array.isArray(itemsKeys)) {
        itemsKeys = [itemsKeys];
    }
    itemsKeys.forEach(itemsKey => {
        clearRulesStartsWith({ useForm, key: `${prefix}${itemsKey}` });
        Object.keys(rules)
            .filter(key => key.startsWith(`${prefix}${itemsKey}`))
            .forEach(key => addRules({ useForm: data.useForm, modelRef: data.useForm.modelRef, key: key, rules: rules[key] }));
    });
};

// need copy customs_node_modules into node_modules
export const watchRulesChildren = ({ props, data, refs, trigger, createRules, itemsKey }) => {
    watch(trigger, () => clearRulesStartsWith({ useForm: data.useForm, key: itemsKey }), { flush: "sync" });
    watch(trigger, () => createRulesChildren({ props, data, refs, rules: createRules(), itemsKey }), { flush: "post" });
};
